import Image from "next/image"
import { formatFocusArea } from "./formatFocusArea"
import { IMAGE_QUALITY_MOBILE, IMAGE_QUALITY_DESKTOP } from "@/lib/heroImageUtils"
import { resolveImageLinkHref } from "@/lib/formatters/formatLinks"

export const renderContentfulImage = (
  src: string,
  alt: string,
  width: number,
  height: number,
  className: string,
  quality: number = IMAGE_QUALITY_MOBILE,
  focusArea?: string,
  componentImagePriority?: "high" | "auto" | "low",
  isLCP: boolean = false,
  /**
   * Layout width per breakpoint. Omit only for images whose rendered width
   * really is `width`: without it next/image emits a 1x/2x DPR srcset, so a
   * DPR>=2 device downloads the 2x candidate even in a viewport-width slot.
   */
  sizes?: string,
) => {
  if (!src || src === "") return null
  const focusParam = focusArea ? formatFocusArea(focusArea) : null
  const imageSrc =
    focusParam != null
      ? src.includes("?")
        ? `${src}&f=${focusParam}`
        : `${src}?f=${focusParam}`
      : src
  // Most callers pass a fixed design-width alongside CSS like `w-full`/
  // `object-cover`, which stretches the image responsively. Without an
  // explicit `sizes`, Next.js can't tell the image is capped at `width`px
  // and falls back to generating a much larger (up to 3840px) candidate —
  // see docs/LIGHTHOUSE_RESPONSE.md #2. Telling it the image is full-width
  // on mobile and capped at its design width above that keeps the
  // generated srcset close to what's actually needed.
  const resolvedSizes =
    sizes ?? (width ? `(max-width: 640px) 100vw, ${width}px` : undefined)
  return (
    <Image
      src={imageSrc}
      alt={alt || " "}
      width={width}
      height={height}
      className={className}
      quality={quality}
      sizes={resolvedSizes}
      fetchPriority={componentImagePriority}
      loading={isLCP ? "eager" : "lazy"}
    />
  )
}

export interface NormalizedImage {
  id: string | null
  type: string
  src: string
  alt: string
  width?: number
  height?: number
  quality?: number
  focusArea?: string
  /** Resolved href from the Image's attached `link` field, if any. */
  href?: string | null
}

export const normalizeImageObject = (image: any, lang?: string) => {
  let src = null
  let alt = null
  if (image?.__typename == "BynderAsset") {
    src = image?.bynderAssetReference?.[0]?.thumbnails?.transformBaseUrl
    alt = image?.altText || image?.title
  } else if (image?.__typename == "Image") {
    src = image?.image?.url
    alt = image?.altText || image?.title
  } else if (image?.__typename == "ImageWithFocalPoint") {
    src = image?.image?.url
    alt = image?.altText || image?.title
  }
  const href =
    image?.__typename == "Image" || image?.__typename == "ImageWithFocalPoint"
      ? resolveImageLinkHref(image?.link, lang)
      : null
  return {
    id: image?.sys?.id || null,
    type: image?.__typename,
    src,
    alt,
    focusArea: image?.focusArea ?? undefined,
    href,
  }
}

/* Gets an image resized as close to the width as possible with the natural ratio */
export const normalizeImgNatural = (
  image: any,
  preferred_width: number,
  quality: number = IMAGE_QUALITY_DESKTOP,
) => {
  let src = image?.image?.url
  let alt = image?.altText || image?.title
  const naturalWidth = image?.image?.width || 0
  const naturalHeight = image?.image?.height || 0
  const ratio = naturalWidth > 0 ? naturalHeight / naturalWidth : 1
  const calculated_width = preferred_width
  const calculated_height = preferred_width * ratio
  if (image?.__typename == "BynderAsset") {
    src = `${src}?io=transform:fit,width:${calculated_width}&quality=${quality}`
  } else if (
    image?.__typename == "Image" ||
    image?.__typename == "ImageWithFocalPoint"
  ) {
    src = image?.image?.url
    alt = image?.altText || image?.title
    src = `${src}?q=${quality}&w=${calculated_width}&fit=scale&f=${formatFocusArea(image?.focusArea)}`
  }
  return {
    id: image?.sys?.id || null,
    type: image?.__typename,
    src,
    alt,
    width: calculated_width,
    height: calculated_height,
    quality: IMAGE_QUALITY_MOBILE,
  }
}
/* Gets an image resized as close to the width as possible and crop down to the right height */
export const normalizeImgFill = (
  image: any,
  width: number,
  height: number,
  quality: number = IMAGE_QUALITY_DESKTOP,
) => {
  let src = image?.image?.url
  let alt = image?.altText || image?.title
  if (image?.__typename == "BynderAsset") {
    src = `${src}?io=transform:fill,width:${width},height:${height}&quality=${quality}&f=${formatFocusArea(image?.focusArea)}`
  } else if (
    image?.__typename == "Image" ||
    image?.__typename == "ImageWithFocalPoint"
  ) {
    src = image?.image?.url
    alt = image?.altText || image?.title
    src = `${src}?q=${quality}&w=${width}&h=${height}&fit=fill&f=${formatFocusArea(image?.focusArea)}`
  }
  return {
    id: image?.sys?.id || null,
    type: image?.__typename,
    src,
    alt,
    width: width,
    height: height,
    quality: IMAGE_QUALITY_MOBILE,
  }
}
/* Gets a smallish square (88x88) image */
export const normalizeImgThumbnail = (
  image: any,
  size: number,
  quality: number = IMAGE_QUALITY_DESKTOP,
) => {
  let src = image?.image?.url
  let alt = image?.altText || image?.title
  if (image?.__typename == "BynderAsset") {
    src = `${src}?io=transform:fill,width:${size},height:${size}&quality=${quality}&f=${formatFocusArea(image?.focusArea)}`
  } else if (
    image?.__typename == "Image" ||
    image?.__typename == "ImageWithFocalPoint"
  ) {
    src = image?.image?.url
    alt = image?.altText || image?.title
    src = `${src}?q=${quality}&w=${size}&h=${size}&fit=thumb`
  }
  return {
    id: image?.sys?.id || null,
    type: image?.__typename,
    src,
    alt,
    width: size,
    height: size,
    quality: IMAGE_QUALITY_MOBILE,
  }
}
